// components/LoanRequestHeader.tsx
import {useEffect, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import CustomHeader from '../../../../../components/mazraeKit/headers/homeHeader';
import CancelLoanBottomSheet from '../../cancelLoanRequest/CancelLoanBottomSheet';
import CancelLoanConfirmModal from '../../cancelLoanRequest/CancelLoanConfirmModal';

export interface LoanRequestHeaderProps {
  /** Navigation back path */
  backPath: string;
  /** Page title to display */
  title: string;
  /** Loan Request GUID for cancellation */
  loanRequestGuid?: string;
  /** Whether to show info icon (cancel button) */
  showCancelButton: boolean;
  /** Callback when cancel is confirmed (optional, overrides default) */
  onCancelConfirm?: () => void;
  /** Optional custom preComponent render */
  customPreComponent?: () => JSX.Element;
  /** Optional custom postComponent render */
  customPostComponent?: () => JSX.Element;
  /** Whether header should be sticky */
  isSticky?: boolean;
  /** Optional callback when sticky state changes */
  onStickyChange?: (isSticky: boolean) => void;
  /** Sticky offset from top (default: 0) */
  stickyOffset?: number;
}

const LoanRequestHeader: React.FC<LoanRequestHeaderProps> = ({
  backPath,
  title,
  loanRequestGuid,
  showCancelButton,
  onCancelConfirm,
  customPreComponent,
  customPostComponent,
  isSticky = true,
  onStickyChange,
  stickyOffset = 0,
}) => {
  const navigate = useNavigate();
  const [isOpenCancelLoanBtm, setIsOpenCancelLoanBtm] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isStuck, setIsStuck] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);

  // Handle sticky behavior
  useEffect(() => {
    if (!isSticky || !headerRef.current) return;

    const header = headerRef.current;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const stuck = entry.intersectionRatio < 1;
        setIsStuck(stuck);
        onStickyChange?.(stuck);
      },
      {
        threshold: [1],
        rootMargin: `-${stickyOffset}px 0px 0px 0px`,
      },
    );

    observer.observe(header);

    return () => {
      observer.disconnect();
    };
  }, [isSticky, stickyOffset, onStickyChange]);

  const handleCancelClick = () => {
    setIsOpenCancelLoanBtm(true);
  };

  const handleCancelConfirm = () => {
    if (onCancelConfirm) {
      onCancelConfirm();
    } else {
      setShowConfirmModal(true);
    }
  };

  // Default back button with title
  const defaultPreComponent = () => (
    <div
      className="relative flex items-center gap-2 cursor-pointer"
      onClick={() => navigate(backPath)}
    >
      <i className="icon-nameArrow---Right text-[25px] text-gray-700" />
      <span className="font-medium">{title}</span>
    </div>
  );

  // Default info/cancel button
  const defaultPostComponent = () => (
    <>
      {showCancelButton && (
        <i
          className="icon-nameInfo-menu-1 text-[25px] text-gray-700 cursor-pointer"
          onClick={handleCancelClick}
        />
      )}
    </>
  );

  return (
    <>
      <div
        ref={headerRef}
        className={`${
          isSticky ? 'sticky top-0 z-50' : ''
        } transition-shadow duration-200 ${isStuck ? 'shadow-md' : ''}`}
      >
        <CustomHeader
          preComponent={customPreComponent || defaultPreComponent}
          postComponent={customPostComponent || defaultPostComponent}
        />
      </div>

      {/* Cancel Loan Bottom Sheet */}
      <CancelLoanBottomSheet
        isOpen={isOpenCancelLoanBtm}
        setIsOpen={setIsOpenCancelLoanBtm}
        onConfirm={handleCancelConfirm}
      />

      {/* Cancel Loan Confirm Modal */}
      {loanRequestGuid && (
        <CancelLoanConfirmModal
          isOpen={showConfirmModal}
          setIsOpen={setShowConfirmModal}
          loanGuid={loanRequestGuid}
        />
      )}
    </>
  );
};

export default LoanRequestHeader;
